import { Fragment } from 'react';
import type { ReactNode } from 'react';

/**
 * The only inline markdown the assistant is allowed to emit: `**bold**` and a bare
 * newline. Deliberately not a markdown library — the surface is two constructs, and a
 * parser that can only produce `<strong>` and `<br>` cannot be coaxed into rendering
 * links, images or raw HTML from model output.
 */
const INLINE_MARKDOWN_RE = /(\*\*[^*]+\*\*|\n)/;

/**
 * One animatable unit of a message. Words are revealed individually; a newline is its
 * own token so a line break is never split across the boundary of a revealed word.
 */
export type WordToken = { kind: 'word'; text: string; bold: boolean } | { kind: 'newline' };

/**
 * Splits text into per-word tokens for the typewriter reveal, preserving which words
 * were emphasised. Whitespace is dropped: the renderer re-inserts a single space
 * between adjacent words, so the reveal never depends on the model's spacing.
 */
export function parseToWordTokens(text: string): WordToken[] {
    const tokens: WordToken[] = [];

    for (const part of text.split(INLINE_MARKDOWN_RE)) {
        if (part === '\n') {
            tokens.push({ kind: 'newline' });
            continue;
        }

        const bold = part.startsWith('**') && part.endsWith('**');
        const words = (bold ? part.slice(2, -2) : part).split(' ').filter(Boolean);

        for (const word of words) {
            tokens.push({ kind: 'word', text: word, bold });
        }
    }

    return tokens;
}

/** Renders the supported inline markdown for the static (non-animated) path. */
export function renderInlineMarkdown(text: string): ReactNode[] {
    return text.split(INLINE_MARKDOWN_RE).map((part, index) => {
        if (part === '\n') {
            return <br key={index} />;
        }
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return <Fragment key={index}>{part}</Fragment>;
    });
}

/** Strips emphasis markers so the accessible label reads as prose, not as markup. */
export function stripInlineMarkdown(text: string): string {
    return text.replace(/\*\*([^*]+)\*\*/g, '$1');
}
